/**
 * SMS list detail — members (add / paste-import / remove) and the drip
 * sequence editor for this list. Route: /sms/lists/:id (customer-scoped
 * variants included).
 *
 * Drip v1 semantics (mirrors backend/src/sms/drip.ts): offsets count from
 * each member's subscribe time; members who joined BEFORE any steps
 * existed are not enrolled retroactively; STOP/DNC always blocks sends.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  smsLists, type SmsListMemberRow, type SmsDripStepRow, type ImportResult,
} from '@/api/smsLists';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useScopedNavigate } from '@/hooks/useScopedNavigate';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { PhoneInput } from '@/components/PhoneInput';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Users, Plus, Upload, Trash2, Search, Timer, Pencil,
  MessageSquareText, UserX,
} from 'lucide-react';
import { cn } from '@/lib/cn';

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];

export function SmsListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useScopedNavigate();
  const user = useCurrentUser();
  const canMutate = MUTATE_ROLES.includes(user.role);

  const listQuery = useQuery({
    queryKey: ['sms-list', id],
    queryFn: () => smsLists.get(id!),
    enabled: Boolean(id),
  });
  const list = listQuery.data;

  if (listQuery.isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading list…</div>;
  }
  if (!list) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-muted-foreground">This list doesn&rsquo;t exist (or was removed).</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/sms')}>
          <ArrowLeft className="h-3.5 w-3.5 me-1.5 rtl:rotate-180" />
          Back to SMS
        </Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={`SMS list · ${formatRef(list.ref_number, 'SML')}`}
        title={list.name}
        description={list.description ?? 'Subscribers receive broadcasts, drip messages, and folder alerts sent to this list.'}
        actions={(
          <Button variant="outline" size="sm" onClick={() => navigate('/sms')}>
            <ArrowLeft className="h-3.5 w-3.5 me-1.5 rtl:rotate-180" />
            All SMS
          </Button>
        )}
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_24rem]">
        <MembersPanel listId={list.id} canMutate={canMutate} />
        <DripPanel listId={list.id} canMutate={canMutate} />
      </div>
    </>
  );
}

// ── Members ──────────────────────────────────────────────────────────────────

function MembersPanel({ listId, canMutate }: { listId: string; canMutate: boolean }) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-list-members', listId, search],
    queryFn: () => smsLists.listMembers(listId, { search: search.trim() || undefined }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (memberId: string) => smsLists.removeMember(memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-list-members', listId] });
      qc.invalidateQueries({ queryKey: ['sms-list', listId] });
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Subscribers
          </CardTitle>
          <CardDescription>
            People who texted a JOIN keyword show up here automatically.
          </CardDescription>
        </div>
        {canMutate && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-3.5 w-3.5 me-1.5" />
              Import
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 me-1.5" />
              Add
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search number or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center">
            <Users className="mx-auto h-6 w-6 text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No matches.' : 'No subscribers yet. Add one, paste an import, or publish a JOIN keyword.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border -my-2">
            {rows.map((m) => (
              <MemberRow
                key={m.id}
                row={m}
                canMutate={canMutate}
                onRemove={() => remove.mutate(m.id)}
                removing={remove.isPending}
              />
            ))}
          </ul>
        )}
      </CardContent>
      {canMutate && (
        <>
          <AddMemberDialog
            listId={listId} customerId={customerId}
            open={addOpen} onOpenChange={setAddOpen}
          />
          <ImportMembersDialog
            listId={listId}
            open={importOpen} onOpenChange={setImportOpen}
          />
        </>
      )}
    </Card>
  );
}

const SOURCE_LABEL: Record<SmsListMemberRow['source'], string> = {
  portal: 'added here',
  keyword: 'texted to join',
  import: 'imported',
};

function MemberRow({
  row, canMutate, onRemove, removing,
}: {
  row: SmsListMemberRow; canMutate: boolean; onRemove: () => void; removing: boolean;
}) {
  const unsubscribed = row.unsubscribed_at !== null;
  return (
    <li className={cn('flex items-center gap-3 py-3', unsubscribed && 'opacity-60')}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm tracking-tight">{row.e164}</span>
          {row.display_name && (
            <span className="text-sm text-muted-foreground truncate">{row.display_name}</span>
          )}
          {unsubscribed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <UserX className="h-3 w-3" />
              Unsubscribed
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {SOURCE_LABEL[row.source]} · since {new Date(row.subscribed_at).toLocaleDateString()}
        </div>
      </div>
      {canMutate && (
        <Button
          size="sm" variant="ghost"
          onClick={onRemove}
          disabled={removing}
          aria-label={`Remove ${row.e164}`}
        >
          <Trash2 className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      )}
    </li>
  );
}

function AddMemberDialog({
  listId, customerId, open, onOpenChange,
}: {
  listId: string; customerId: string | null;
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => smsLists.addMember(listId, {
      e164: phone.trim(),
      display_name: name.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-list-members', listId] });
      qc.invalidateQueries({ queryKey: ['sms-list', listId] });
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
      setPhone(''); setName(''); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Add failed.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add subscriber</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="mem-phone">Phone number</Label>
            <PhoneInput
              id="mem-phone"
              value={phone}
              onChange={setPhone}
              inputClassName="font-mono"
              customerId={customerId ?? undefined}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mem-name">Name (optional)</Label>
            <Input
              id="mem-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={add.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={add.isPending || !phone.trim()}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Adding…' : 'Add subscriber'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportMembersDialog({
  listId, open, onOpenChange,
}: {
  listId: string; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => smsLists.importMembers(listId, text),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['sms-list-members', listId] });
      qc.invalidateQueries({ queryKey: ['sms-list', listId] });
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
      setResult(r);
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Import failed.'),
  });

  function close(v: boolean) {
    onOpenChange(v);
    if (!v) { setText(''); setResult(null); setError(null); }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import subscribers</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="py-2 space-y-3">
            <p className="text-sm">
              <span className="font-semibold text-emerald-700">{result.inserted} added</span>
              {result.resubscribed > 0 && <> · {result.resubscribed} re-subscribed</>}
              {result.existing > 0 && <> · {result.existing} already on the list</>}
              {result.failed.length > 0 && (
                <> · <span className="font-semibold text-rose-700">{result.failed.length} failed</span></>
              )}
            </p>
            {result.failed.length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-md border border-border p-2 text-xs space-y-1">
                {result.failed.map((f) => (
                  <li key={`${f.line}-${f.raw}`} className="flex gap-2">
                    <span className="text-muted-foreground shrink-0">line {f.line}:</span>
                    <span className="font-mono truncate">{f.raw}</span>
                    <span className="text-rose-600 shrink-0">{f.error}</span>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" size="sm" onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-2 py-2">
              <Label htmlFor="import-text">One subscriber per line</Label>
              <Textarea
                id="import-text" rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="font-mono text-xs"
                placeholder={'555-123-4567, Rivka G\n+15551230002, Levi\n555-123-0003'}
              />
              <p className="text-xs text-muted-foreground">
                Format: <span className="font-mono">phone, name</span> (name
                optional). Paste from a spreadsheet — commas or tabs both work.
                Numbers already on the list are skipped, not duplicated.
              </p>
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={run.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                type="button"
                disabled={run.isPending || !text.trim()}
                onClick={() => run.mutate()}
              >
                <Upload className="h-3.5 w-3.5 me-1.5" />
                {run.isPending ? 'Importing…' : 'Import'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Drip sequence ────────────────────────────────────────────────────────────

function formatOffset(hours: number): string {
  if (hours === 0) return 'Immediately';
  if (hours % 24 === 0) {
    const d = hours / 24;
    return `Day ${d}`;
  }
  if (hours < 24) return `After ${hours}h`;
  return `After ${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function DripPanel({ listId, canMutate }: { listId: string; canMutate: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SmsDripStepRow | 'new' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-drip-steps', listId],
    queryFn: () => smsLists.listDripSteps(listId),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (stepId: string) => smsLists.removeDripStep(stepId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-drip-steps', listId] });
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
    },
  });

  return (
    <Card className="h-fit">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm inline-flex items-center gap-2">
            <Timer className="h-4 w-4 text-muted-foreground" />
            Drip sequence
          </CardTitle>
          <CardDescription>
            Automatic texts after someone subscribes. Applies to members who
            join once steps exist.
          </CardDescription>
        </div>
        {canMutate && (
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4 me-1.5" />
            Step
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center">
            <MessageSquareText className="mx-auto h-6 w-6 text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground max-w-[16rem] mx-auto">
              No steps yet. Example: a welcome text immediately, then a
              reminder on day 3.
            </p>
          </div>
        ) : (
          <ol className="relative space-y-4 ps-5 before:absolute before:inset-y-1 before:start-1.5 before:w-px before:bg-border">
            {rows.map((s) => (
              <li key={s.id} className="relative">
                <span
                  aria-hidden
                  className={cn(
                    'absolute -start-5 top-1 h-3 w-3 rounded-full ring-2 ring-background',
                    s.active ? 'bg-primary' : 'bg-slate-300',
                  )}
                />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {formatOffset(s.offset_hours)}
                      </span>
                      {!s.active && (
                        <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          off
                        </span>
                      )}
                    </div>
                    <p className={cn('mt-0.5 text-sm', !s.active && 'text-muted-foreground')}>
                      {s.body}
                    </p>
                  </div>
                  {canMutate && (
                    <div className="flex shrink-0">
                      <Button
                        size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => setEditing(s)}
                        aria-label="Edit step"
                      >
                        <Pencil className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => remove.mutate(s.id)}
                        disabled={remove.isPending}
                        aria-label="Delete step"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
        <p className="mt-4 text-[11px] text-muted-foreground">
          Times count from each subscriber&rsquo;s join moment. Unsubscribing or
          texting STOP stops the sequence.
        </p>
      </CardContent>
      {canMutate && editing !== null && (
        <DripStepDialog
          listId={listId}
          step={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function DripStepDialog({
  listId, step, onClose,
}: {
  listId: string; step: SmsDripStepRow | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [days, setDays] = useState(step ? String(Math.floor(step.offset_hours / 24)) : '0');
  const [hours, setHours] = useState(step ? String(step.offset_hours % 24) : '0');
  const [body, setBody] = useState(step?.body ?? '');
  const [active, setActive] = useState(step?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const totalHours = (Number(days) || 0) * 24 + (Number(hours) || 0);

  const save = useMutation({
    mutationFn: () => (step
      ? smsLists.updateDripStep(step.id, { offset_hours: totalHours, body: body.trim(), active })
      : smsLists.createDripStep(listId, { offset_hours: totalHours, body: body.trim(), active })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-drip-steps', listId] });
      qc.invalidateQueries({ queryKey: ['sms-lists'] });
      onClose();
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{step ? 'Edit drip step' : 'New drip step'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Send after subscribing</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" min={0} max={365}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-20 tabular-nums"
                aria-label="Days"
              />
              <span className="text-sm text-muted-foreground">days</span>
              <Input
                type="number" min={0} max={23}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-20 tabular-nums"
                aria-label="Hours"
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
            <p className="text-xs text-muted-foreground">
              0 days 0 hours = sent within a minute of subscribing.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="step-body">Message</Label>
            <Textarea
              id="step-body" rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1600}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </label>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={save.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={save.isPending || !body.trim() || totalHours < 0}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : step ? 'Save step' : 'Add step'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
